import { ALL_MENU_OBJECT_DATA } from 'routers/routes.data';

import {
  dashBoardV2ContentConst,
  menuBarV2ContentConst,
  menuBarV2SubMapContentConst,
  menuBarV2SubRouteContentConst,
  menuBarV2SubSearchContentConst,
} from 'constants/common/menu/menuBarContentConst';
import featureGuideJson from 'jsons/content/guide/featureGuide.json';

import { getServiceType, removeSlashPath } from './pathUtil';
import { getPageByKey } from './routePageUtil';

const buildFeatureGuideMapper = () => {
  const mapper = {};
  const services = ['map', 'search', 'route'];
  const subMenus = {
    map: menuBarV2SubMapContentConst,
    search: menuBarV2SubSearchContentConst,
    route: menuBarV2SubRouteContentConst,
  };

  services.forEach((key) => {
    const guide = featureGuideJson.services[key];
    const subMenu = subMenus[key];
    
    mapper[key] = {
      headline: guide.headline,
      title: guide.title,
      getItems: [
        {
          id: guide.intro.id,
          title: guide.intro.title,
          content: guide.intro.contentArray, // lnbUtil only checks for existence
        },
        ...subMenu.map((item) => ({
          id: key === 'route' ? (item.id || item.title) : item.id,
          title: item.title,
          content: item.description?.replace(/\n/g, ''),
          options: (item.content || []).map((sub) => ({
            id: key === 'route' && sub.path === '/web-route-normal' ? '탐색' : sub.title,
            title: sub.title,
            path: sub.path,
            image: sub.image || sub.guideImage,
            content: sub.description,
            createdAt: sub.createdAt,
            updatedAt: sub.updatedAt,
          })),
        })),
      ],
    };
  });
  return mapper;
};

const FEATURE_GUIDE_MAPPER_CONST = buildFeatureGuideMapper();

/**
 * @desc 지도 서비스 전용 LNB를 구성합니다.
 */
const getMapLnb = (guidePath, guideData) => {
  const items = guideData.getItems || [];
  const mapGroups =
    menuBarV2ContentConst['서비스'][0]?.groups
      .filter((el) => el.label === 'API 및 SDK')[0]
      .sections.filter((el) => el.title === '지도')[0] || [];
  const allApis = flattenApis(mapGroups.apis);

  // 1. 시작하기
  const startGuide = {
    title: '시작하기',
    path: '/map-start-guide',
  };

  // 2. 기능 소개  (items[0])를 2번째에 배치
  const introItem = items[0];
  const functionalGuide = {
    title: guideData.headline || '기능 소개 ',
    path: guidePath?.replace(/\/\*$/, ''),
    tagType: introItem?.tagType,
    createdAt: introItem?.createdAt || introItem?.createAt,
    updatedAt: introItem?.updatedAt || introItem?.updateAt,
    content: introItem?.options
      ? introItem.options.map((opt) => ({
          title: opt.title,
          path: opt.path?.replace(/\/\*$/, ''),
          createdAt: opt.createdAt || opt.createAt,
          updatedAt: opt.updatedAt || opt.updateAt,
        }))
      : null,
  };

  // 3. 개발 가이드, API 레퍼런스 (allApis[1, 2])
  const devGuides = allApis.slice(2, 3);

  // 4. 지도, 마커, 피처, 팝업, Wizard (items[1:])
  const featureGuides = [...items, getPageByKey('wizard')]
    .slice(1)
    .map((item) => ({
      title: item.title,
      path: item.path?.replace(/\/\*$/, '') || '',
      tagType: item.tagType,
      createdAt: item.createdAt || item.createAt,
      updatedAt: item.updatedAt || item.updateAt,
      content: item.options
        ? item.options.map((opt) => ({
            title: opt.title,
            path: opt.path?.replace(/\/\*$/, ''),
            createdAt: opt.createdAt || opt.createAt,
            updatedAt: opt.updatedAt || opt.updateAt,
          }))
        : null,
    }));

  // 5. 나머지 SDK 항목들 (Static Map, ANDROID, IOS 등)
  const otherSdks = allApis.slice(3);

  return {
    title: guideData.title || '',
    path: '/map-feature-guide',
    lnb: [
      startGuide,
      functionalGuide,
      ...devGuides,
      ...featureGuides,
      ...otherSdks,
    ],
  };
};

/**
 * @desc 현재 경로와 일치하는 메뉴 항목을 찾고 해당 LNB 정보를 반환합니다.
 * @param {string} pathname - 현재 브라우저 경로
 * @returns {object} { title, path, lnb }
 */
export const findActiveLnb = (pathname) => {
  // 카드 등록 성공/실패 시 LNB가 유지되도록 경로 정규화
  const cleanPathname = pathname.replace(/\/(success|fail)$/, '');
  const normalizedPathname = removeSlashPath(cleanPathname);

  // Start Guide paths를 Feature Guide paths로 내부 매핑 (LNB 데이터 공유를 위해)
  const pathMapper = {
    '/map-start-guide': 'map',
    '/search-start-guide': 'search',
    '/route-start-guide': 'route',
  };
  const lookupPath = pathMapper[normalizedPathname] || normalizedPathname;

  // 1. 기능 소개  (지도, 검색, 탐색) 우선 매칭
  for (const [key, guideData] of Object.entries(FEATURE_GUIDE_MAPPER_CONST)) {
    const guidePath = `/${key}-feature-guide`;
    const items = guideData.getItems || [];

    // 추가 페이지 정의 (Wizard, Result Code)
    let extraPages = [];
    if (guideData.title === '지도') {
      extraPages.push(getPageByKey('wizard'));
    } else if (guideData.title === '검색') {
      extraPages.push(getPageByKey('result_search_code'));
    } else if (guideData.title === '경로') {
      extraPages.push(getPageByKey('result_route_code'));
    }

    const isMatched =
      normalizedPathname.startsWith(
        removeSlashPath(guidePath?.replace(/\/\*$/, ''))
      ) ||
      lookupPath === key ||
      lookupPath.startsWith(removeSlashPath(guidePath?.replace(/\/\*$/, ''))) ||
      items.some(
        (item) =>
          removeSlashPath(item.path?.replace(/\/\*$/, '')) ===
            normalizedPathname ||
          (item.options &&
            item.options.some(
              (opt) =>
                removeSlashPath(opt.path?.replace(/\/\*$/, '')) ===
                normalizedPathname
            ))
      ) ||
      extraPages.some(
        (page) =>
          page &&
          removeSlashPath(page.path?.replace(/\/\*$/, '')) ===
            normalizedPathname
      );

    if (isMatched) {
      if (guideData.title === '지도') {
        return getMapLnb(guidePath, guideData);
      }

      // 기본 LNB 아이템 구성
      const startGuidePath = `/${key}-start-guide`;

      const lnbItems = [
        { title: '시작하기', path: startGuidePath },
        ...items.map((item) => ({
          title: item.title === '소개' ? guideData.headline : item.title,
          path:
            item.id === 'intro'
              ? guidePath
              : item.path?.replace(/\/\*$/, '') || '',
          tagType: item.tagType,
          createdAt: item.createdAt || item.createAt,
          updatedAt: item.updatedAt || item.updateAt,
          content: item.options
            ? item.options.map((opt) => ({
                title: opt.title,
                path: opt.path?.replace(/\/\*$/, ''),
                createdAt: opt.createdAt || opt.createAt,
                updatedAt: opt.updatedAt || opt.updateAt,
              }))
            : null,
        })),
      ];

      // 추가 페이지(Result Code)를 LNB에 추가
      extraPages.forEach((page) => {
        if (page) {
          lnbItems.push({
            title: page.title,
            path: page.path?.replace(/\/\*$/, ''),
            tagType: page.tagType, // 필요한 경우
            createdAt: page.createdAt || page.createAt,
            updatedAt: page.updatedAt || page.updateAt,
            content: null,
          });
        }
      });

      return {
        title: guideData.title || '',
        path: guidePath, // path 추가
        lnb: lnbItems,
      };
    }
  }

  // 2. 대시보드 및 단순 섹션 구조 매칭
  const allContents = { ...menuBarV2ContentConst, ...dashBoardV2ContentConst };

  for (const [key, value] of Object.entries(allContents)) {
    if (key === '서비스') continue;

    const sections = value[0]?.sections || (Array.isArray(value) ? value : []);

    const normalizedSections =
      Array.isArray(value) && !value[0]?.sections ? value : sections;

    const isMatched = normalizedSections.some(
      (s) =>
        removeSlashPath(s.path?.replace(/\/\*$/, '')) === normalizedPathname ||
        // 동적 라우트 지원: /community/:idx 같은 경로를 /community로 매칭
        (s.path &&
          normalizedPathname.startsWith(
            removeSlashPath(s.path?.replace(/\/\*$/, '')) + '/'
          )) ||
        (s.content &&
          s.content.some(
            (c) =>
              removeSlashPath(c.path?.replace(/\/\*$/, '')) ===
                normalizedPathname ||
              (c.path &&
                normalizedPathname.startsWith(
                  removeSlashPath(c.path?.replace(/\/\*$/, '')) + '/'
                ))
          ))
    );

    if (isMatched) {
      return {
        title: key === 'CONSOLE' ? '콘솔' : key,
        path: '', // TODO: 적절한 경로 설정이 필요할 수 있음
        lnb: normalizedSections.map((s) => ({
          title: s.title,
          path: s.path?.replace(/\/\*$/, ''),
          link: s.link,
          type: s.type,
          tagType: s.tagType,
          isNewTab: s.isNewTab,
          isAbsolute: s.isAbsolute,
          createdAt: s.createdAt || s.createAt,
          updatedAt: s.updatedAt || s.updateAt,
          content: s.content
            ? s.content.map((c) => ({
                title: c.title,
                path: c.path?.replace(/\/\*$/, ''),
                link: c.link,
                type: c.type,
                isNewTab: c.isNewTab,
                isAbsolute: c.isAbsolute,
                createdAt: c.createdAt || c.createAt,
                updatedAt: c.updatedAt || c.updateAt,
              }))
            : null,
        })),
      };
    }
  }

  // 3. 복잡한 구조 (서비스 - API 상세 등)
  const serviceGroups = menuBarV2ContentConst['서비스'][0]?.groups || [];
  for (const group of serviceGroups) {
    for (const section of group.sections) {
      const apisObj = section.apis || section.verticals;

      if (apisObj) {
        const allApis = flattenApis(apisObj);
        const isMatched =
          allApis.some(
            (api) => removeSlashPath(api.path) === normalizedPathname
          ) || removeSlashPath(section.path) === normalizedPathname;

        if (isMatched) {
          if (section.title === '지도') {
            const guidePath = 'map';
            const guideData = FEATURE_GUIDE_MAPPER_CONST[guidePath];
            return getMapLnb(guidePath, guideData);
          }
          return {
            title: section.title,
            path: section.path,
            lnb: allApis,
          };
        }
      } else if (removeSlashPath(section.path) === normalizedPathname) {
        return {
          title: section.title,
          path: section.path,
          lnb: [],
        };
      }
    }
  }

  // 4. LeftSideMenu fallback
  const parentPath = findParentPathFromLeftSideMenu(cleanPathname);
  if (parentPath) {
    return findActiveLnb(parentPath);
  }

  return { title: '', path: '', lnb: [] };
};

/**
 * @desc API 그룹들을 평면화된 리스트로 변환합니다.
 */
const flattenApis = (apis) => {
  const result = [];

  apis.forEach((api) => {
    const processItem = (item) => {
      const pageKey = item.pageKey || item.key;
      const pageInfo = pageKey ? getPageByKey(pageKey) : {};

      let path = (item.path || pageInfo?.path)?.replace(/\/\*$/, '');
      return {
        title: item.title || pageInfo?.title,
        path: path,
        type: item.type,
        isAbsolute: item.isAbsolute,
        isNewTab: item.isNewTab,
        createdAt: item.createdAt || pageInfo?.createdAt || item.createAt || pageInfo?.createAt,
        updatedAt: item.updatedAt || pageInfo?.updatedAt || item.updateAt || pageInfo?.updateAt,
        tagType: item.tagType || pageInfo?.tagType,
      };
    };

    if (api.content) {
      api.content.forEach((c) => {
        result.push(processItem(c));
      });
    } else if (api.path || api.key || api.pageKey) {
      result.push(processItem(api));
    }
  });
  return result;
};

/**
 * @desc 현재 경로가 leftSideMenu 그룹에 속하는지 확인하고, 부모(그룹 첫 번째) 경로를 반환합니다.
 */
export const findParentPathFromLeftSideMenu = (pathname) => {
  const normalizedPath = removeSlashPath(pathname);

  for (const menuData of Object.values(ALL_MENU_OBJECT_DATA)) {
    if (!menuData.pages || !menuData.leftSideMenu) continue;

    let matchedKey = null;

    // 1. 현재 경로와 일치하는 페이지 키 찾기
    for (const [key, page] of Object.entries(menuData.pages)) {
      if (!page.path) continue;
      const pagePath = removeSlashPath(page.path?.replace(/\/\*$/, ''));

      // 동적 라우트 처리 (예: /community/:idx)
      const regexPath = pagePath.replace(/:[^\s/]+/g, '([^/]+)');
      const regex = new RegExp(`^${regexPath}$`);

      if (regex.test(normalizedPath)) {
        matchedKey = key;
        break;
      }
    }

    if (!matchedKey) continue;

    // 2. leftSideMenu 및 navigation_mobile 그룹 확인
    const targets = [menuData.leftSideMenu, menuData.navigation_mobile];

    for (const targetMenu of targets) {
      if (!targetMenu) continue;

      for (const group of Object.values(targetMenu)) {
        if (Array.isArray(group) && group.includes(matchedKey)) {
          // 그룹의 첫 번째 항목(메인)의 경로 반환
          const parentKey = group[0];
          const parentPage = menuData.pages[parentKey];
          if (
            parentPage &&
            parentPage.path &&
            removeSlashPath(parentPage.path?.replace(/\/\*$/, '')) !==
              normalizedPath
          ) {
            return parentPage.path?.replace(/\/\*$/, '');
          }
        }
      }
    }
  }
  return null;
};
